import { CalendarIcon, Link2Icon, ListTreeIcon, ShapesIcon } from "lucide-react";
import type { CmmdBoardTask, CmmdWorkItemType } from "@cmmd-forge/contracts";
import type { ReactNode } from "react";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { ProjectTaskPicker } from "./ProjectTaskPicker";
import {
  createTaskLabel,
  createTaskOptions,
  createWorkItemTypeOptions,
  optionalTaskId,
  taskIdValue,
} from "./projectTaskCreateOptions";

export interface ProjectTaskCreatePropertiesProps {
  readonly tasks: readonly CmmdBoardTask[];
  readonly parentTaskId: number | null;
  readonly workItemType: CmmdWorkItemType;
  readonly dueDate: string;
  readonly dependencyIds: readonly number[];
  readonly disabled: boolean;
  readonly onParentTaskIdChange: (taskId: number | null) => void;
  readonly onWorkItemTypeChange: (workItemType: CmmdWorkItemType) => void;
  readonly onDueDateChange: (date: string) => void;
  readonly onDependencyIdsChange: (ids: readonly number[]) => void;
}

export function ProjectTaskCreateProperties(props: ProjectTaskCreatePropertiesProps) {
  const dependencyOptions = createTaskOptions(props.tasks).filter(
    (option) => !props.dependencyIds.includes(Number(option.value)),
  );
  return (
    <div
      id="task-create-more-properties"
      className="grid gap-3 border-t border-border pt-4 md:grid-cols-2 md:gap-4"
    >
      <TaskCreateProperty label="Parent" icon={<ListTreeIcon aria-hidden />}>
        <ProjectTaskPicker
          ariaLabel="Parent task"
          value={taskIdValue(props.parentTaskId)}
          emptyLabel="No parent"
          options={createTaskOptions(props.tasks)}
          disabled={props.disabled}
          emptyVisual={{ kind: "task" }}
          onValueChange={(value) => props.onParentTaskIdChange(optionalTaskId(value))}
        />
      </TaskCreateProperty>
      <TaskCreateProperty label="Type" icon={<ShapesIcon aria-hidden />}>
        <ProjectTaskPicker
          ariaLabel="Work item type"
          value={props.workItemType}
          emptyLabel="Task"
          options={createWorkItemTypeOptions()}
          disabled={props.disabled}
          onValueChange={(value) =>
            props.onWorkItemTypeChange((value ?? "other") as CmmdWorkItemType)
          }
        />
      </TaskCreateProperty>
      <TaskCreateProperty label="Due date" icon={<CalendarIcon aria-hidden />}>
        <Input
          type="date"
          aria-label="Due date"
          value={props.dueDate}
          disabled={props.disabled}
          onChange={(event) => props.onDueDateChange(event.currentTarget.value)}
        />
      </TaskCreateProperty>
      <TaskCreateProperty label="Dependencies" icon={<Link2Icon aria-hidden />}>
        <ProjectTaskPicker
          ariaLabel="Add dependency"
          value={null}
          emptyLabel="Add dependency"
          options={dependencyOptions}
          disabled={props.disabled || dependencyOptions.length === 0}
          emptyVisual={{ kind: "task" }}
          onValueChange={(value) => {
            const id = optionalTaskId(value);
            if (id !== null) props.onDependencyIdsChange([...props.dependencyIds, id]);
          }}
        />
      </TaskCreateProperty>
      {props.dependencyIds.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5 md:col-span-2" aria-label="Selected dependencies">
          {props.dependencyIds.map((id) => (
            <li key={id}>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full"
                onClick={() =>
                  props.onDependencyIdsChange(
                    props.dependencyIds.filter((dependencyId) => dependencyId !== id),
                  )
                }
              >
                <Link2Icon aria-hidden />
                {createTaskLabel(id, props.tasks)}
                <span className="sr-only">; remove</span>
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function TaskCreateProperty(props: {
  readonly label: string;
  readonly icon: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <label className="grid gap-1.5">
      <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground [&_svg]:size-3.5">
        {props.icon}
        {props.label}
      </span>
      {props.children}
    </label>
  );
}
